import { fillerColDef } from "@/lib/fillerColDef";
import { returnStringFormattedDateTime } from "@/lib/globalFormatterHelper";
import { defaultColumnWidths } from "@/lib/interfaces and Enums/ENUMColumnWidths";
import { InstrumentTypeCode } from "@/lib/interfaces and Enums/ENUMInstrumentTypeCode";
import { TableHeaderField } from "@/lib/interfaces and Enums/ENUMTableHeaders";
import { saveCreationBlockValuesToDatabase } from "@/services/CreationTableSERVICES";
import { ColDef, NewValueParams } from "ag-grid-community";
import { BlockSource } from "@/components/widgets/tradeFromScratch/helper/blockEditHelper";
import CreationEffectiveDateCellRenderer from "../cellRenderers/CreationEffectiveDateCellRenderer";
import CreationSecurityCellRenderer from "../cellRenderers/CreationSecurityCellRenderer";
import NotionalCellRenderer from "../cellRenderers/NotionalCellRenderer";
import RiskDV01CellRenderer from "../cellRenderers/RiskDV01CellRenderer";

export function returnCreationTableColDefs(getAccessTokenSilently: Function) {
  const creationTableColDefs: (ColDef & Record<string, any>)[] = [
    // group cell renderer needed for expand / collapse icons
    {
      filter: false,
      checkboxSelection: true,
      width: defaultColumnWidths.checkbox + defaultColumnWidths.expandArrow,
      fixedWidth: true, // don't scale with font — keep fixed at 53px like the blotter's checkbox/expand column
      resizable: false,
      suppressMovable: true,
      // pinned: "left",
      lockPosition: true, // This ensures the column stays in the first position
      suppressColumnsToolPanel: true,
      cellRenderer: "agGroupCellRenderer",
      headerCheckboxSelectionFilteredOnly: true,
      headerCheckboxSelection: true,
      headerClass: "CreationCheckboxHeader fixedWidthHeader",
    },
    {
      field: "direction",
      headerName: TableHeaderField.direction,
      headerClass: "CreationDirectionHeader",
      colId: "CreationDirectionCell",
      filterParams: {
        values: ["Buy", "Sell", "Pay", "Receive"],
      },
      width: defaultColumnWidths.side,
      cellDataType: "text",
    },
    {
      field: "notionalLeg1",
      headerName: TableHeaderField.notionalLeg1,
      headerClass: "CreationNotionalHeader",
      colId: "CreationNotionalCell",
      cellDataType: "number",
      filter: "agNumberColumnFilter",
      width: defaultColumnWidths.nominal,
      cellRenderer: NotionalCellRenderer,
    },
    {
      field: "notionalLeg2",
      headerName: TableHeaderField.notionalLeg2,
      headerClass: "CreationNotionalLeg2Header",
      colId: "CreationNotionalLeg2Cell",
      cellDataType: "number",
      filter: "agNumberColumnFilter",
      width: defaultColumnWidths.nominal,
      cellRenderer: NotionalCellRenderer,
      initialHide: true,
    },
    {
      field: "securityName",
      headerName: TableHeaderField.securityName,
      headerClass: "CreationSecurityHeader",
      colId: "CreationSecurityCell",
      cellDataType: "text",
      width: defaultColumnWidths.name,
      // Draggable so a block can be dragged out onto the Swap Curve widget (LQC-1607).
      cellRenderer: CreationSecurityCellRenderer,
    },
    // LQC-227: ISIN column — always present, empty for non-bond blocks
    {
      field: "isin",
      headerName: "ISIN",
      headerClass: "CreationISINHeader",
      colId: "CreationISINCell",
      cellDataType: "text",
      width: defaultColumnWidths.isin,
    },
    {
      field: "effectiveDate",
      editable: true,
      headerName: TableHeaderField.effectiveDate,
      headerClass: "CreationSettlementHeader",
      colId: "CreationSettlementCell",
      cellDataType: "dateString",
      width: defaultColumnWidths.date,
      minWidth: defaultColumnWidths.date,
      cellStyle: { padding: "0px" },
      cellClass: "ag-roundedInputStyle",
      cellRenderer: CreationEffectiveDateCellRenderer,
      //commented out because saveCreationBlockValues was breaking (table data not saving correctly, and therefore not going to the endpoint)
      // valueSetter: (params: any) => {
      //   saveCreationBlockValuesToDatabase(params, getAccessTokenSilently);
      //   return false;
      // },
      onCellValueChanged: (params: NewValueParams) => {
        if (params.newValue == null || params.newValue == undefined) {
          params.newValue = params.oldValue;
          params.data.effectiveDate = params.oldValue;
        }
        saveCreationBlockValuesToDatabase(params, getAccessTokenSilently);
      },
    },
    {
      field: "dV01",
      headerName: TableHeaderField.dV01,
      headerClass: "CreationDV01Header",
      colId: "CreationDV01Cell",
      cellDataType: "number",
      filter: "agNumberColumnFilter",
      cellRenderer: RiskDV01CellRenderer,
      // valueFormatter: (params) => {
      //   if (params.value) {
      //     return returnDV0_Formatted(params.value);
      //   }
      //   return "0";
      // },
      width: defaultColumnWidths.dv01,
    },
    {
      field: "extStructureId",
      headerName: TableHeaderField.extStructureId,
      extraScaling: true,
      headerClass: "CreationExternalIDHeader",
      colId: "CreationExternalIDCell",
      cellDataType: "text",
      filter: "agTextColumnFilter",
      width: defaultColumnWidths.extStructureId,
    },
    {
      field: "dateCreated",
      headerName: TableHeaderField.dateCreated,
      headerClass: "CreationDateCreatedHeader",
      colId: "CreationDateCreatedCell",
      filter: "agDateColumnFilter",
      filterParams: {
        buttons: ["reset"],
        comparator: (filterLocalDateAtMidnight: Date, cellValue: Date) => {
          if (!cellValue) return -1;

          const cellDate = new Date(cellValue);
          const cellYear = cellDate.getFullYear();
          const cellMonth = cellDate.getMonth(); // 0-based
          const cellDay = cellDate.getDate();

          const filterYear = filterLocalDateAtMidnight.getFullYear();
          const filterMonth = filterLocalDateAtMidnight.getMonth();
          const filterDay = filterLocalDateAtMidnight.getDate();

          if (cellYear < filterYear) return -1;
          if (cellYear > filterYear) return 1;

          if (cellMonth < filterMonth) return -1;
          if (cellMonth > filterMonth) return 1;

          if (cellDay < filterDay) return -1;
          if (cellDay > filterDay) return 1;
          return 0; // Exact match - T
        },
      },
      valueFormatter: (params) => {
        if (params.value) {
          return returnStringFormattedDateTime(params.value);
        } else return "";
      },
      width: defaultColumnWidths.actionedInfo,
      // sort: "desc",
    },
    {
      filter: false,
      field: "newIssue",
      colId: "CreationNewIssue",
      headerName: TableHeaderField.newIssue,
      extraScaling: true,
      width: defaultColumnWidths.newIssue,
      editable: true,
      cellStyle: { display: "flex", justifyContent: "center" },
      cellClass: (params: any) => {
        //only show checkbox cell if below instrumentTypes, otherwise show nothing
        const instrumentType = params.data.instrumentType;
        if (
          instrumentType == InstrumentTypeCode.BND ||
          instrumentType == InstrumentTypeCode.IXB ||
          instrumentType == InstrumentTypeCode.ABS
        ) {
          return "";
        } else return "hideCell";
      },
      onCellValueChanged: (params: NewValueParams) => {
        saveCreationBlockValuesToDatabase(params, getAccessTokenSilently);
      },
    },
    {
      field: "owner",
      headerName: TableHeaderField.owner,
      headerClass: "CreationOwnerHeader",
      colId: "CreationOwnerCell",
      cellDataType: "text",
      filter: "agTextColumnFilter",
      initialHide: false,
      width: defaultColumnWidths.actionedInfo,
    },
    {
      filter: true,
      field: "status",
      colId: "CreationStatus",
      width: defaultColumnWidths.status,
      editable: false,
    },
    // LQC-3027 - the block's origin (LQC-3094), for testers and users to tell blocks apart.
    {
      field: "source",
      headerName: TableHeaderField.source,
      headerClass: "CreationSourceHeader",
      colId: "CreationSourceCell",
      cellDataType: "text",
      filterParams: {
        values: Object.values(BlockSource),
      },
      initialHide: true,
      width: defaultColumnWidths.status,
    },
    ...fillerColDef,
  ];
  return creationTableColDefs;
}
